import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { ActionDefinition, ActionExecution } from '@/types/action';
import { getAccessToken } from '@/utils/jwt';

export const actionsApi = createApi({
  reducerPath: 'actionsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Action', 'Execution'],
  endpoints: (builder) => ({
    listActions: builder.query<ActionDefinition[], undefined>({
      query: () => '/actions',
      transformResponse: (response: { actions: ActionDefinition[] }) => response.actions,
      providesTags: ['Action'],
    }),
    getAction: builder.query<ActionDefinition, number>({
      query: (id) => `/actions/${String(id)}`,
      transformResponse: (response: { action: ActionDefinition }) => response.action,
      providesTags: (_r, _e, id) => [{ type: 'Action', id }],
    }),
    executeAction: builder.mutation<
      ActionExecution,
      {
        actionId: number;
        parameters: Record<string, unknown>;
        userId: string;
        conversationId?: string;
      }
    >({
      query: ({ actionId, parameters, userId, conversationId }) => ({
        url: `/actions/${String(actionId)}/execute`,
        method: 'POST',
        body: { parameters, user_id: userId, conversation_id: conversationId ?? '' },
      }),
      transformResponse: (response: { execution: ActionExecution }) => response.execution,
      invalidatesTags: ['Execution'],
    }),
    dryRunAction: builder.mutation<
      Record<string, unknown>,
      { actionId: number; parameters: Record<string, unknown> }
    >({
      query: ({ actionId, parameters }) => ({
        url: `/actions/${String(actionId)}/dry-run`,
        method: 'POST',
        body: { parameters },
      }),
      transformResponse: (response: { dry_run: Record<string, unknown> }) => response.dry_run,
    }),
    listExecutions: builder.query<ActionExecution[], undefined>({
      query: () => '/executions',
      transformResponse: (response: { executions: ActionExecution[] }) => response.executions,
      providesTags: ['Execution'],
    }),
    getExecution: builder.query<ActionExecution, number>({
      query: (id) => `/executions/${String(id)}`,
      transformResponse: (response: { execution: ActionExecution }) => response.execution,
      providesTags: (_r, _e, id) => [{ type: 'Execution', id }],
    }),
    approveExecution: builder.mutation<
      ActionExecution,
      { id: number; approverId: string; approverRole?: string }
    >({
      query: ({ id, approverId, approverRole }) => ({
        url: `/executions/${String(id)}/approve`,
        method: 'POST',
        body: { approver_id: approverId, approver_role: approverRole ?? 'user' },
      }),
      transformResponse: (response: { execution: ActionExecution }) => response.execution,
      invalidatesTags: ['Execution'],
    }),
    rejectExecution: builder.mutation<
      ActionExecution,
      { id: number; approverId: string; reason?: string }
    >({
      query: ({ id, approverId, reason }) => ({
        url: `/executions/${String(id)}/reject`,
        method: 'POST',
        body: { approver_id: approverId, reason },
      }),
      transformResponse: (response: { execution: ActionExecution }) => response.execution,
      invalidatesTags: ['Execution'],
    }),
  }),
});

export const {
  useListActionsQuery,
  useGetActionQuery,
  useExecuteActionMutation,
  useDryRunActionMutation,
  useListExecutionsQuery,
  useGetExecutionQuery,
  useApproveExecutionMutation,
  useRejectExecutionMutation,
} = actionsApi;
